import type { Vec2 } from "../types";

/** A steering-intent judgment from the Jev `/api/steer` channel, already stamped with
 * the CLIENT's clock at receipt (see `client/steer/client.ts`'s module doc comment —
 * same clock-mismatch fix as `FocusJudgment`, the server's own `t` is never used). */
export interface SteerJudgment {
  intent: "left" | "right" | "up" | "down" | "hold";
  confidence: number;
  reliable: number;
  /** ms, client clock, receipt time. */
  t: number;
}

export interface AssistConfig {
  /** Max fraction of velocity direction blended toward the judged intent (0..1). */
  assistMax: number;
  /** Speed multiplier floor applied when `reliable` is at/below 0 (never a hard freeze). */
  dampFloor: number;
  /** `reliable` at/above this gets full (undamped) speed. */
  reliableKnee: number;
  /** A judgment older than this (ms, vs. the `now` passed to `blendAssist`) is stale. */
  maxAgeMs: number;
}

export const ASSIST_DEFAULTS: AssistConfig = {
  assistMax: 0.45,
  dampFloor: 0.35,
  reliableKnee: 0.5,
  maxAgeMs: 2000,
};

export interface AssistResult {
  velocity: Vec2;
  assist: number;
  damp: number;
  reason: "none" | "stale" | "unreliable" | "hold" | "closed" | "assisting";
}

const HOLD_VECTOR: Vec2 = { x: 0, y: 0 };

/** Screen axes: up = -y, down = +y, left = -x, right = +x (matches `src/types.ts`'s
 * documented gaze convention). */
const INTENT_VECTORS: Record<SteerJudgment["intent"], Vec2> = {
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  hold: HOLD_VECTOR,
};

function magnitude(v: Vec2): number {
  return Math.hypot(v.x, v.y);
}

function normalize(v: Vec2): Vec2 {
  const m = magnitude(v);
  if (m === 0) return { x: 0, y: 0 };
  return { x: v.x / m, y: v.y / m };
}

/**
 * Blends `velocity`'s direction toward a judged steering intent, scaled by confidence
 * and reading reliability, then damps overall speed when the reading is unreliable.
 * Pure — see `AssistConfig`'s fields and the module's task-level spec for the exact
 * rules; summarized:
 *
 * - No judgment, or one older than `cfg.maxAgeMs`: velocity unchanged, assist 0, damp 1
 *   (`reason` "none" for a null judgment, "stale" for an aged-out one).
 * - Otherwise `damp` ramps from `dampFloor` (at `reliable <= 0`) to 1 (at
 *   `reliable >= reliableKnee`).
 * - `assist` is 0 when the intent is "hold" (`reason` "hold") or the intended
 *   direction isn't in `open` (`reason` "closed"); otherwise
 *   `assist = assistMax * confidence * reliable` (`reason` "assisting").
 * - A stationary cursor (`|velocity| === 0`) always stays `{0, 0}` — `assist` is still
 *   reported, just never applied to a cursor that isn't moving.
 * - Otherwise the output direction is `normalize(normalize(velocity) * (1 - assist) +
 *   intentUnitVector * assist)`, at the original speed, scaled by `damp`. (If that
 *   blend degenerates to the zero vector — velocity and intent exactly opposed at
 *   assist 0.5 — the original direction is kept rather than producing NaN; this can't
 *   happen at the default `assistMax` of 0.45.)
 */
export function blendAssist(
  velocity: Vec2,
  judgment: SteerJudgment | null,
  open: { left: boolean; right: boolean; up: boolean; down: boolean },
  now: number,
  cfg: Partial<AssistConfig> = {},
): AssistResult {
  const config = { ...ASSIST_DEFAULTS, ...cfg };

  if (judgment === null) {
    return { velocity, assist: 0, damp: 1, reason: "none" };
  }
  if (now - judgment.t > config.maxAgeMs) {
    return { velocity, assist: 0, damp: 1, reason: "stale" };
  }

  const damp =
    judgment.reliable >= config.reliableKnee
      ? 1
      : config.dampFloor + (1 - config.dampFloor) * (judgment.reliable / config.reliableKnee);

  let assist: number;
  let reason: AssistResult["reason"];
  if (judgment.intent === "hold") {
    assist = 0;
    reason = "hold";
  } else if (!open[judgment.intent]) {
    assist = 0;
    reason = "closed";
  } else {
    assist = config.assistMax * judgment.confidence * judgment.reliable;
    reason = "assisting";
  }

  const speed = magnitude(velocity);
  if (speed === 0) {
    return { velocity: { x: 0, y: 0 }, assist, damp, reason };
  }

  const dir = normalize(velocity);
  const intentVec = INTENT_VECTORS[judgment.intent];
  const blended = {
    x: dir.x * (1 - assist) + intentVec.x * assist,
    y: dir.y * (1 - assist) + intentVec.y * assist,
  };
  const blendedDir = normalize(blended);
  const outDir = blendedDir.x === 0 && blendedDir.y === 0 ? dir : blendedDir;

  return {
    velocity: { x: outDir.x * speed * damp, y: outDir.y * speed * damp },
    assist,
    damp,
    reason,
  };
}
